import { Injectable } from '@angular/core';

import { FsMapServiceProvider, IFsMap } from '@lib/geo/core';

/**
 * Map Facade Service
 *
 * This service provides a provider-agnostic interface for map operations,
 * routing calls to the selected map provider implementation.
 */
@Injectable()
export class MapFacadeService<TMap extends IFsMap> extends FsMapServiceProvider<TMap> {
  constructor(private readonly _provider: FsMapServiceProvider<TMap>) {
    super();
  }

  get geospatialProvider(): string {
    return this._provider.geospatialProvider;
  }

  get id(): string {
    return this._provider.id;
  }

  getZoom(): number {
    return this._provider.getZoom();
  }

  setZoom(zoom: number): void {
    this._provider.setZoom(zoom);
  }

  getCenter() {
    return this._provider.getCenter();
  }

  setCenter(coords: any): void {
    this._provider.setCenter(coords);
  }

  getBounds() {
    return this._provider.getBounds();
  }

  setStyle(style: string): void {
    this._provider.setStyle(style);
  }

  getStyle(): string {
    return this._provider.getStyle();
  }

  setOptions(options: any): void {
    this._provider.setOptions(options);
  }

  panTo(coords: any): void {
    this._provider.panTo(coords);
  }

  setHeading(heading: number): void {
    this._provider.setHeading(heading);
  }

  on(event: string) {
    return this._provider.on(event);
  }

  addMarker(marker: any): void {
    this._provider.addMarker(marker);
  }

  removeMarker(marker: any): void {
    this._provider.removeMarker(marker);
  }
  addLayer(layer: any): void {
    this._provider.addLayer(layer);
  }

  removeLayer(layer: any): void {
    this._provider.removeLayer(layer);
  }

  setMapId(mapId: string): void {
    this._provider.setMapId(mapId);
  }

  removeLayers(): void {
    this._provider.removeLayers();
  }

  get isRoomClearingEnabled(): boolean {
    return this._provider.isRoomClearingEnabled;
  }

  get activePopoutCount(): number {
    return this._provider.activePopoutCount;
  }

  get axonDeviceMarkers(): any[] {
    return this._provider.axonDeviceMarkers;
  }

  setAlertBoundaryStatus(status: string): void {
    this._provider.setAlertBoundaryStatus(status);
  }

  placeMarker(coords: { lng: number; lat: number }): void {
    this._provider.placeMarker(coords);
  }

  onLoaded(fsMap: TMap): void {
    this._provider.onLoaded(fsMap);
  }

  goTo(type: string, target: any, action?: string): void {
    this._provider.goTo(type, target, action);
  }

  goToCoordinates(coords: { lng: number; lat: number }, zoom: number = 18): void {
    this._provider.goToCoordinates(coords, zoom);
  }

  activateLocation(locationId: string, move: boolean = true, keepAlarmPopup?: boolean): void {
    this._provider.activateLocation(locationId, move, keepAlarmPopup);
  }

  deactivateLocation(): void {
    this._provider.deactivateLocation();
  }

  zoomIn(): void {
    this._provider.zoomIn();
  }

  zoomOut(): void {
    this._provider.zoomOut();
  }

  slewTo(coords: { lng: number; lat: number }): void {
    this._provider.slewTo(coords);
  }

  isLayerSelected(type: string): boolean {
    return this._provider.isLayerSelected(type);
  }

  toggleInnerLayer(type: string): void {
    this._provider.toggleInnerLayer(type);
  }

  addShape(type: string, action: string, source: string): void {
    this._provider.addShape(type, action, source);
  }

  setDrawingMode(overlayType?: any): void {
    this._provider.setDrawingMode(overlayType);
  }

  isModeActive(mode: string, source: string): boolean {
    return this._provider.isModeActive(mode, source);
  }

  setCamerasParams(params: any): void {
    this._provider.setCamerasParams(params);
  }

  setLprMakesParams(params: any, isLayerOn?: boolean): void {
    this._provider.setLprMakesParams(params, isLayerOn);
  }

  toggleRoomClearing(): void {
    this._provider.toggleRoomClearing();
  }

  removeAllCadPopouts(): void {
    this._provider.removeAllCadPopouts();
  }

  manageAlertBoundary(): void {
    this._provider.manageAlertBoundary();
  }

  closedAlarmListener(alarm: any): void {
    this._provider.closedAlarmListener(alarm);
  }

  // Google Maps specific properties for compatibility
  get mapInstance(): any {
    return (this._provider as any).mapInstance;
  }

  getInstance(): any {
    return (this._provider as any).getInstance();
  }

  get mapOptions(): any {
    return (this._provider as any).mapOptions;
  }

  get layers(): any[] {
    return (this._provider as any).layers || [];
  }

  set layers(value: any[]) {
    if ((this._provider as any).layers) {
      (this._provider as any).layers = value;
    }
  }

  get layerChanged$(): any {
    return (this._provider as any).layerChanged$;
  }

  get drawingSource(): string {
    return (this._provider as any).drawingSource;
  }

  set drawingSource(value: string) {
    if ((this._provider as any).drawingSource !== undefined) {
      (this._provider as any).drawingSource = value;
    }
  }

  get isAlertBoundaryOpen(): boolean {
    return (this._provider as any).isAlertBoundaryOpen || false;
  }

  set isAlertBoundaryOpen(value: boolean) {
    if ((this._provider as any).isAlertBoundaryOpen !== undefined) {
      (this._provider as any).isAlertBoundaryOpen = value;
    }
  }

  get isDrawingAlertBoundary(): boolean {
    return (this._provider as any).isDrawingAlertBoundary || false;
  }

  get isInitializing$(): any {
    return (this._provider as any)._isInitializing$;
  }
}
